import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Modal } from 'react-bootstrap';
import { Formik } from 'formik';
import { FiBriefcase, FiHome, FiInfo, FiLock, FiMapPin, FiUser } from 'react-icons/fi';

import api from '../../../../services/api';

import {
  FieldBlock,
  FooterButton,
  FooterHint,
  Grid,
  Label,
  ModalSubtitle,
  OriginAddressBlock,
  Section,
  SectionHead,
  Select,
  TextInput,
  ToggleChip,
  ToggleGroup,
} from './styles';

interface IWorker {
  id: string;
  cpf: string;
  first_name: string;
  last_name: string;
  rg?: string;
  activity_profile?: 'rural' | 'industrial' | 'market' | 'public-service';
  is_peasant?: boolean;
  is_outsourced?: boolean;
  worker_function?: string;
  genre: 'male' | 'female' | 'other';
  phone: string;
  situation?: 'temporary' | 'permanent';
  email?: string;
  address?: string;
  city_id?: number;
  state?: string;
  postal_code?: string;
  is_temporary_address?: boolean;
  origin_address?: string;
  origin_city_id?: number;
  origin_state?: string;
  origin_postal_code?: string;
  salary_range?: number;
  is_unionized?: boolean;
  is_syndicate_approved: boolean | null;
  syndicate_approved_at: Date;
  syndicate_id?: string;
  city: {
    nome: string;
  };
  syndicate: {
    id: string;
    nome_fantasia: string;
  };
  company?: {
    id: string;
    nome_fantasia: string;
    razao_social: string;
    cnpj: string;
    address: string;
    state: string;
    postal_code: string;
  };
  is_discount_agreed: boolean;
  is_lgpd_agreed: boolean;
  is_privacy_terms_agreed: boolean;
  created_at: Date;
}

interface IBGECities {
  id: number;
  nome: string;
}

interface IBGEStates {
  id: number;
  nome: string;
  uf: string;
}

interface IProps {
  showModal: boolean;
  handleShowModal: (value: boolean) => void;
  worker: IWorker;
  handleWorker: (worker: IWorker) => void;
}

const EditWorkerModal: React.FC<IProps> = ({
  showModal,
  handleShowModal,
  worker,
  handleWorker,
}) => {
  const [states, setStates] = useState<IBGEStates[]>([]);
  const [cities, setCities] = useState<IBGECities[]>([]);
  const [originCities, setOriginCities] = useState<IBGECities[]>([]);
  const [companies, setCompanies] = useState<{ id: string; nome_fantasia: string }[]>([]);

  const handleUpdateWorker = useCallback(
    async values => {
      await api.put(`/workers/${worker.id}`, values);
      const fresh = await api.get(`/workers/${worker.id}`);
      handleWorker(fresh.data);
      handleShowModal(false);
    },
    [worker, handleShowModal, handleWorker],
  );

  useEffect(() => {
    const loadStates = async () => {
      const loadedStates = await api.get('/ibge/states');
      setStates(loadedStates.data);
    };
    loadStates();
  }, []);

  useEffect(() => {
    const loadCompanies = async () => {
      const loaded = await api.get('/companies', {
        params: { syndicate_id: worker?.syndicate_id },
      });
      setCompanies(loaded.data);
    };
    loadCompanies();
  }, [worker?.syndicate_id]);

  useEffect(() => {
    const loadCities = async () => {
      if (worker?.state) {
        const loadedCities = await api.get(`/ibge/cities/${worker.state}`);
        setCities(loadedCities.data);
      }
    };
    loadCities();
  }, [worker]);

  useEffect(() => {
    const loadOriginCities = async () => {
      if (worker?.origin_state) {
        const loadedCities = await api.get(
          `/ibge/cities/${worker.origin_state}`,
        );
        setOriginCities(loadedCities.data);
      }
    };
    loadOriginCities();
  }, [worker]);

  const initialValues = useMemo(
    () => ({
      cpf: worker?.cpf,
      name: worker ? `${worker.first_name} ${worker.last_name}` : '',
      rg: worker?.rg,
      activity_profile: worker?.activity_profile,
      is_peasant: worker?.is_peasant,
      is_outsourced: worker?.is_outsourced,
      worker_function: worker?.worker_function,
      genre: worker?.genre,
      phone: worker?.phone,
      situation: worker?.situation,
      email: worker?.email,
      address: worker?.address,
      city_id: worker?.city_id,
      state: worker?.state,
      postal_code: worker?.postal_code,
      is_temporary_address: worker?.is_temporary_address,
      origin_address: worker?.origin_address,
      origin_city_id: worker?.origin_city_id,
      origin_state: worker?.origin_state,
      origin_postal_code: worker?.origin_postal_code,
      salary_range: worker?.salary_range,
      is_unionized: worker?.is_unionized,
      syndicate_id: worker?.syndicate_id,
      company_id: worker?.company?.id,
      password: '',
      password_confirmation: '',
    }),
    [worker],
  );

  return (
    <Modal
      show={showModal}
      onHide={() => handleShowModal(false)}
      aria-labelledby="contained-modal-title-vcenter"
      centered
      size="xl"
    >
      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={handleUpdateWorker}
      >
        {({ values, handleSubmit, handleChange, setFieldValue }) => {
          const showSafrista = values.activity_profile === 'rural';
          const showTerceirizado = values.activity_profile === 'industrial';

          return (
            <form onSubmit={handleSubmit}>
              <Modal.Header closeButton>
                <div>
                  <Modal.Title style={{ fontSize: 18, fontWeight: 700 }}>
                    Editar trabalhador
                  </Modal.Title>
                  <ModalSubtitle>
                    Atualize os dados do trabalhador. Todos os campos podem ser
                    ajustados a qualquer momento.
                  </ModalSubtitle>
                </div>
              </Modal.Header>

              <Modal.Body style={{ background: '#fafbfc', padding: 20 }}>
                {/* 1 · Dados pessoais */}
                <Section>
                  <SectionHead>
                    <span className="badge">
                      <FiUser size={16} />
                    </span>
                    <div>
                      <h4>Dados pessoais</h4>
                      <span className="subtitle">
                        Identificação e contato do trabalhador
                      </span>
                    </div>
                  </SectionHead>

                  <Grid $cols={2}>
                    <FieldBlock $span={2}>
                      <Label>Nome completo</Label>
                      <TextInput
                        type="text"
                        placeholder="Digite o nome completo"
                        value={values.name || ''}
                        onChange={handleChange('name')}
                      />
                    </FieldBlock>
                  </Grid>

                  <Grid $cols={3}>
                    <FieldBlock>
                      <Label>CPF</Label>
                      <TextInput
                        type="text"
                        placeholder="000.000.000-00"
                        value={values.cpf || ''}
                        onChange={handleChange('cpf')}
                      />
                    </FieldBlock>
                    <FieldBlock>
                      <Label>RG</Label>
                      <TextInput
                        type="text"
                        placeholder="Digite o RG"
                        value={values.rg || ''}
                        onChange={handleChange('rg')}
                      />
                    </FieldBlock>
                    <FieldBlock>
                      <Label>Gênero</Label>
                      <Select
                        value={values.genre || ''}
                        onChange={handleChange('genre')}
                      >
                        <option value="">Selecione</option>
                        <option value="male">Masculino</option>
                        <option value="female">Feminino</option>
                        <option value="other">Outro</option>
                      </Select>
                    </FieldBlock>
                  </Grid>

                  <Grid $cols={2}>
                    <FieldBlock>
                      <Label>Telefone</Label>
                      <TextInput
                        type="text"
                        placeholder="(00) 00000-0000"
                        value={values.phone || ''}
                        onChange={handleChange('phone')}
                      />
                    </FieldBlock>
                    <FieldBlock>
                      <Label>E-mail</Label>
                      <TextInput
                        type="email"
                        placeholder="nome@exemplo.com"
                        value={values.email || ''}
                        onChange={handleChange('email')}
                      />
                    </FieldBlock>
                  </Grid>
                </Section>

                {/* 2 · Perfil de atividade */}
                <Section>
                  <SectionHead>
                    <span className="badge">
                      <FiBriefcase size={16} />
                    </span>
                    <div>
                      <h4>Perfil de atividade</h4>
                      <span className="subtitle">
                        Área, função e vínculo de trabalho
                      </span>
                    </div>
                  </SectionHead>

                  <Grid $cols={2}>
                    <FieldBlock>
                      <Label>Perfil de atividade</Label>
                      <Select
                        value={values.activity_profile || ''}
                        onChange={e => {
                          setFieldValue('activity_profile', e.target.value);
                          if (e.target.value !== 'rural') {
                            setFieldValue('is_peasant', undefined);
                          }
                          if (e.target.value !== 'industrial') {
                            setFieldValue('is_outsourced', undefined);
                          }
                        }}
                      >
                        <option value="">Selecione</option>
                        <option value="rural">Rural</option>
                        <option value="industrial">Industrial</option>
                        <option value="market">Comércio</option>
                        <option value="public-service">Serviço Público</option>
                      </Select>
                    </FieldBlock>

                    <FieldBlock>
                      <Label>Situação</Label>
                      <Select
                        value={values.situation || ''}
                        onChange={handleChange('situation')}
                      >
                        <option value="">Selecione</option>
                        <option value="temporary">Temporário</option>
                        <option value="permanent">Permanente</option>
                      </Select>
                    </FieldBlock>
                  </Grid>

                  {showSafrista && (
                    <FieldBlock>
                      <Label>Safrista</Label>
                      <ToggleGroup>
                        <ToggleChip
                          type="button"
                          $active={values.is_peasant === true}
                          onClick={() => setFieldValue('is_peasant', true)}
                        >
                          <span className="marker" />
                          Sim
                        </ToggleChip>
                        <ToggleChip
                          type="button"
                          $active={values.is_peasant === false}
                          onClick={() => setFieldValue('is_peasant', false)}
                        >
                          <span className="marker" />
                          Não
                        </ToggleChip>
                      </ToggleGroup>
                    </FieldBlock>
                  )}

                  {showTerceirizado && (
                    <FieldBlock>
                      <Label>Terceirizado</Label>
                      <ToggleGroup>
                        <ToggleChip
                          type="button"
                          $active={values.is_outsourced === true}
                          onClick={() => setFieldValue('is_outsourced', true)}
                        >
                          <span className="marker" />
                          Sim
                        </ToggleChip>
                        <ToggleChip
                          type="button"
                          $active={values.is_outsourced === false}
                          onClick={() => setFieldValue('is_outsourced', false)}
                        >
                          <span className="marker" />
                          Não
                        </ToggleChip>
                      </ToggleGroup>
                    </FieldBlock>
                  )}

                  <Grid $cols={2}>
                    <FieldBlock>
                      <Label>Função</Label>
                      <TextInput
                        type="text"
                        placeholder="Ex.: Operador de máquina"
                        value={values.worker_function || ''}
                        onChange={handleChange('worker_function')}
                      />
                    </FieldBlock>
                    <FieldBlock>
                      <Label>Faixa salarial</Label>
                      <Select
                        value={values.salary_range ?? ''}
                        onChange={handleChange('salary_range')}
                      >
                        <option value="">Selecione</option>
                        <option value="1">Menos que 1 salário mínimo</option>
                        <option value="2">Até 1 salário mínimo</option>
                        <option value="3">De 1 a 2 salários mínimos</option>
                        <option value="4">De 2 a 4 salários mínimos</option>
                        <option value="5">Maior que 4 salários mínimos</option>
                      </Select>
                    </FieldBlock>
                  </Grid>
                </Section>

                {/* 3 · Endereço */}
                <Section>
                  <SectionHead>
                    <span className="badge">
                      <FiMapPin size={16} />
                    </span>
                    <div>
                      <h4>Endereço</h4>
                      <span className="subtitle">
                        Localização atual e de origem do trabalhador
                      </span>
                    </div>
                  </SectionHead>

                  <Grid $cols={4}>
                    <FieldBlock>
                      <Label>CEP</Label>
                      <TextInput
                        type="text"
                        placeholder="00000-000"
                        value={values.postal_code || ''}
                        onChange={handleChange('postal_code')}
                      />
                    </FieldBlock>
                    <FieldBlock $span={3}>
                      <Label>Endereço</Label>
                      <TextInput
                        type="text"
                        placeholder="Rua, número e complemento"
                        value={values.address || ''}
                        onChange={handleChange('address')}
                      />
                    </FieldBlock>
                  </Grid>

                  <Grid $cols={2}>
                    <FieldBlock>
                      <Label>Estado</Label>
                      <Select
                        value={values.state || ''}
                        onChange={async e => {
                          setFieldValue('state', e.target.value);
                          setFieldValue('city_id', undefined);
                          if (e.target.value) {
                            const loaded = await api.get(
                              `/ibge/cities/${e.target.value}`,
                            );
                            setCities(loaded.data);
                          } else {
                            setCities([]);
                          }
                        }}
                      >
                        <option value="">Selecione</option>
                        {states.map(state => (
                          <option key={state.id} value={state.uf}>
                            {state.nome}
                          </option>
                        ))}
                      </Select>
                    </FieldBlock>
                    <FieldBlock>
                      <Label>Cidade</Label>
                      <Select
                        value={values.city_id ?? ''}
                        onChange={handleChange('city_id')}
                        disabled={!values.state}
                      >
                        <option value="">Selecione</option>
                        {cities.map(city => (
                          <option key={city.id} value={city.id}>
                            {city.nome}
                          </option>
                        ))}
                      </Select>
                    </FieldBlock>
                  </Grid>

                  <FieldBlock>
                    <Label>Endereço temporário?</Label>
                    <ToggleGroup>
                      <ToggleChip
                        type="button"
                        $active={values.is_temporary_address === true}
                        onClick={() => setFieldValue('is_temporary_address', true)}
                      >
                        <span className="marker" />
                        Sim
                      </ToggleChip>
                      <ToggleChip
                        type="button"
                        $active={values.is_temporary_address === false}
                        onClick={() => setFieldValue('is_temporary_address', false)}
                      >
                        <span className="marker" />
                        Não
                      </ToggleChip>
                    </ToggleGroup>
                  </FieldBlock>

                  {values.is_temporary_address === true && (
                    <OriginAddressBlock>
                      <span className="block-title">Endereço de origem</span>

                      <Grid $cols={4}>
                        <FieldBlock>
                          <Label>CEP de origem</Label>
                          <TextInput
                            type="text"
                            placeholder="00000-000"
                            value={values.origin_postal_code || ''}
                            onChange={handleChange('origin_postal_code')}
                          />
                        </FieldBlock>
                        <FieldBlock $span={3}>
                          <Label>Endereço de origem</Label>
                          <TextInput
                            type="text"
                            placeholder="Rua, número e complemento"
                            value={values.origin_address || ''}
                            onChange={handleChange('origin_address')}
                          />
                        </FieldBlock>
                      </Grid>

                      <Grid $cols={2}>
                        <FieldBlock>
                          <Label>Estado de origem</Label>
                          <Select
                            value={values.origin_state || ''}
                            onChange={async e => {
                              setFieldValue('origin_state', e.target.value);
                              setFieldValue('origin_city_id', undefined);
                              if (e.target.value) {
                                const loaded = await api.get(
                                  `/ibge/cities/${e.target.value}`,
                                );
                                setOriginCities(loaded.data);
                              } else {
                                setOriginCities([]);
                              }
                            }}
                          >
                            <option value="">Selecione</option>
                            {states.map(state => (
                              <option key={state.id} value={state.uf}>
                                {state.nome}
                              </option>
                            ))}
                          </Select>
                        </FieldBlock>
                        <FieldBlock>
                          <Label>Cidade de origem</Label>
                          <Select
                            value={values.origin_city_id ?? ''}
                            onChange={handleChange('origin_city_id')}
                            disabled={!values.origin_state}
                          >
                            <option value="">Selecione</option>
                            {originCities.map(city => (
                              <option key={city.id} value={city.id}>
                                {city.nome}
                              </option>
                            ))}
                          </Select>
                        </FieldBlock>
                      </Grid>
                    </OriginAddressBlock>
                  )}
                </Section>

                {/* 4 · Empresa */}
                <Section>
                  <SectionHead>
                    <span className="badge">
                      <FiHome size={16} />
                    </span>
                    <div>
                      <h4>Empresa</h4>
                      <span className="subtitle">
                        Vínculo empregatício do trabalhador
                      </span>
                    </div>
                  </SectionHead>

                  <Grid $cols={1}>
                    <FieldBlock>
                      <Label>Empresa</Label>
                      <Select
                        value={values.company_id ?? ''}
                        onChange={handleChange('company_id')}
                      >
                        <option value="">Selecione a empresa</option>
                        {companies.map(company => (
                          <option key={company.id} value={company.id}>
                            {company.nome_fantasia}
                          </option>
                        ))}
                      </Select>
                    </FieldBlock>
                  </Grid>
                </Section>

                {/* 5 · Segurança */}
                <Section>
                  <SectionHead>
                    <span className="badge">
                      <FiLock size={16} />
                    </span>
                    <div>
                      <h4>Segurança</h4>
                      <span className="subtitle">
                        Preencha apenas se quiser redefinir a senha de acesso
                      </span>
                    </div>
                  </SectionHead>

                  <Grid $cols={2}>
                    <FieldBlock>
                      <Label>Nova senha</Label>
                      <TextInput
                        type="password"
                        placeholder="Deixe em branco para manter a atual"
                        value={values.password || ''}
                        onChange={handleChange('password')}
                      />
                    </FieldBlock>
                    <FieldBlock>
                      <Label>Confirmação de senha</Label>
                      <TextInput
                        type="password"
                        placeholder="Repita a nova senha"
                        value={values.password_confirmation || ''}
                        onChange={handleChange('password_confirmation')}
                      />
                    </FieldBlock>
                  </Grid>
                </Section>

                <FooterHint>
                  <FiInfo size={13} />
                  <span>
                    As alterações entram em vigor imediatamente após salvar.
                    Campos deixados em branco serão mantidos como estão.
                  </span>
                </FooterHint>
              </Modal.Body>

              <Modal.Footer>
                <FooterButton
                  type="button"
                  $variant="secondary"
                  onClick={() => handleShowModal(false)}
                >
                  Cancelar
                </FooterButton>
                <FooterButton type="submit">Salvar alterações</FooterButton>
              </Modal.Footer>
            </form>
          );
        }}
      </Formik>
    </Modal>
  );
};

export default EditWorkerModal;
